import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { withNamespaces } from 'react-i18next';
import i18next from 'i18next';
import { formatDate, toISOTimezone, toDisplayDecimal, getContrastColor } from '@utils';
import DescriptionWeekDay from '@supervisor/punch/DescriptionWeekDay'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleExclamation, faCircleXmark, faCircle1, faCircle2 } from "@fortawesome/pro-duotone-svg-icons";
import * as PunchStatusEntries from '@scripts/PunchApproveStatus';
import * as StatusEntries from '@scripts/StatusEntries';
import * as storage from '@storage';
import * as ApprobationStructure from '@scripts/ApprobationStructure';
import * as PunchType from '@scripts/PunchTypeSubCode';

const useStyles = makeStyles((theme) => ({
    ul: {
        '& li': {
            display: 'inline-block',
            margin: '0.5px'
        },
        '& li:last-of-type': {
            borderColor: '#7d7d7d !important',
        }
    },
    spanDayDesc: {
        display: 'table',
        padding: '3px',
        fontWeight: 'bold',
        width: '55px',
        textAlign: 'center'
    },
    spanDay: {
        display: 'table',
        width: '100%'
    },
    spanHour: {
        display: 'table',
        width: '55px'
    },
    spanHourLabel: {
        display: 'table',
        width: '100%',
        fontSize: '12px'
    },
    textBold: {
        fontWeight: 'bold'
    },
    divDay: {
        backgroundColor: '#e9e9e9',
        border: '1px solid #c3c3c3 !important',
        borderTopLeftRadius: '5px',
        borderTopRightRadius: '5px'
    },
    divHour: {
        padding: '10px 0',
        borderWidth: '0 1px 1px 1px',
        borderStyle: 'solid',
        borderBottomLeftRadius: '5px',
        borderBottomRightRadius: '5px',
        borderColor: '#c3c3c3 !important',
        height: '59px'
    },
    vacation: {
        backgroundColor: `${storage.getVacColor()}`
    },
    absence: {
        backgroundColor: `${storage.getAbsColor()}`
    },
    none: {
        color: '#919191'
    },
    iconApproved: {
        color: '#008000',
        fontSize: '16px',
        '& .fa-secondary': {
            opacity: '0.3'
        }
    },
    iconApprovedLevel1: {
        color: '#008000',
        fontSize: '16px',
        '& .fa-secondary': {
            opacity: '0.3'
        }
    },
    iconNotApprovedLevel1: {
        color: '#ff0000',
        fontSize: '16px',
        '& .fa-secondary': {
            opacity: '0.3'
        }
    },
    iconWarning: {
        color: '#9d9a00',
        fontSize: '16px',
        '& .fa-secondary': {
            opacity: '0.3'
        }
    },
    iconError: {
        color: '#ff0000',
        fontSize: '16px',
        '& .fa-secondary': {
            opacity: '0.3'
        }
    },
    divApproved: {
        position: 'relative',
        top: '5px'
    }
}));

function WeekDaysCalendar(props) {
    const [intervalDate, setIntervalDate] = React.useState([]);
    const [data, setData] = React.useState(null);
    const [category, setCategory] = React.useState(null);
    const useDoubleApprobation = storage.getUseDoubleApprobation();
    const approbationStructure = storage.getApprobationStructure();
    const isLevel2Approbator = storage.getIsLevel2Approbator();
    const isLevel1ApprobationMandatory = storage.getIsApprobationLv1Mandatory();

    const classes = useStyles();

    useEffect(() => {
        loadIntervalDate(props.intervalDate.startDate, props.intervalDate.endDate);
    }, [props.intervalDate]);

    useEffect(() => {
        setData(props.data);
    }, [props.data]);

    useEffect(() => {
        setCategory(props.category);
    }, [props.category]);

    const loadIntervalDate = (startDate, endDate) => {
        const listDate = [];

        for (let d = new Date(startDate); d <= new Date(endDate); d.setDate(d.getDate() + 1)) {
            listDate.push({ date: new Date(d) });
        }
        setIntervalDate(listDate);
    }

    const getInfoDate = (date) => {
        if (data != null) {
            const dt = formatDate(date, 'date');
            const objLv1 = data.dataLv1?.filter(x => formatDate(x.dt, 'date') == dt);
            const objLv2 = data.dataLv2?.filter(x => formatDate(x.dt, 'date') == dt);
            const objAp = data.dataApproved?.filter(x => formatDate(x.dt, 'date') == dt);
            let hours = 0;

            hours = objLv1?.reduce((sum, item) => sum + item.hr, 0) ?? 0;
            hours += objLv2?.reduce((sum, item) => sum + item.hr, 0) ?? 0;
            hours += objAp?.reduce((sum, item) => sum + item.hr, 0) ?? 0;

            const objPunch = getWorstCasePunch(objLv1, objLv2, objAp, 1);

            const finalStatus = objPunch.status
            const punchType = objPunch.punchType;

            return { hr: toDisplayDecimal(hours, 2), status: finalStatus, punchType: punchType };
        }

        return { hr: toDisplayDecimal(0, 2), status: PunchStatusEntries.STATUS_NONE, punchType: punchType };
    }

    const getWorstCasePunch = (list1, list2, listAp, typeLevel) => {
        const vacType = list1?.some(item => item.pcType === PunchType.VACATION) ||
            list2?.some(item => item.pcType === PunchType.VACATION) ||
            listAp?.some(item => item.pcType === PunchType.VACATION);

        const absType = list1?.some(item => item.pcType === PunchType.ABSENCE) ||
            list2?.some(item => item.pcType === PunchType.ABSENCE) ||
            listAp?.some(item => item.pcType === PunchType.ABSENCE);

        let isApproved
        let hasLevel1NotApproved;
        let hasLevel2NotApproved;
        let punchType;
        let status;
        let hasError = false;

        if (vacType) {
            punchType = PunchType.VACATION;
        } else if (absType) {
            punchType = PunchType.ABSENCE;
        } else {
            punchType = PunchType.REGULAR;
        }

        if (useDoubleApprobation) {
            if (approbationStructure == ApprobationStructure.DEPARTMENT) {
                // By department

                if (!isLevel2Approbator) {
                    if (!isLevel1ApprobationMandatory) {
                        // Level 1 - Level 1 approbation NOT mandatory
                        // 1 - A green check sticker is displayed during the day if all the punches for the day are approved at Level 1 or 2
                        // 2 - If one of the punches is not approved at Level 1 or 2 during the day, no sticker is displayed
                        hasLevel1NotApproved = list1?.length > 0;
                        isApproved = listAp?.length > 0;
                        if (hasLevel1NotApproved) {
                            // 2 - If one of the punches is not approved at Level 1 or 2 during the day, no sticker is displayed
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        } else if (isApproved) {
                            // 1 - A green check sticker is displayed during the day if all the punches for the day are approved at Level 1 or 2
                            status = PunchStatusEntries.STATUS_APPROVED;
                        }
                    } else {
                        // Level 1 - Level 1 approbation mandatory
                        // 1 - A green check sticker is displayed during the day if all the day's punches are approved (Level 1)
                        // 2 - If one of the punches is not approved (Level 1), no sticker is displayed.
                        hasLevel1NotApproved = list1?.length > 0;
                        isApproved = listAp?.length > 0;
                        if (hasLevel1NotApproved) {
                            // 2 - If one of the punches is not approved (Level 1), no sticker is displayed.
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        } else if (isApproved) {
                            // 1 - A green check sticker is displayed during the day if all the day's punches are approved (Level 1)
                            status = PunchStatusEntries.STATUS_APPROVED;
                        }
                    }
                } else {
                    if (!isLevel1ApprobationMandatory) {
                        // Level 2 - Level 1 approbation NOT mandatory
                        // 1 - Green check sticker is displayed during the day if all the punches for the day are approved level 2
                        // 2 - If one of the punches is not approved at Level 1 or 2, no sticker is displayed.
                        hasLevel1NotApproved = list1?.length > 0;
                        hasLevel2NotApproved = list2?.length > 0;
                        isApproved = listAp?.length > 0;
                        if (hasLevel1NotApproved || hasLevel2NotApproved) {
                            // 2 - If one of the punches is not approved at Level 1 or 2, no sticker is displayed.
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        } else if (isApproved) {
                            // 1 - Green check sticker is displayed during the day if all the punches for the day are approved level 2
                            status = PunchStatusEntries.STATUS_APPROVED;
                        }
                    } else {
                        // Level 2 - Level 1 approbation mandatory
                        // 1 - A green check sticker is displayed during the day if all the day's punches are approved level 2
                        // 2 - If one of the punches is not approved at Level 1, a red "1" sticker is displayed.
                        // 3 - If all Level 1 punches are approved, a green "1" sticker is displayed.
                        hasLevel1NotApproved = list1?.length > 0;
                        hasLevel2NotApproved = list2?.length > 0;
                        isApproved = listAp?.length > 0;
                        if (hasLevel1NotApproved) {
                            // 2 - If one of the punches is not approved at Level 1, a red "1" sticker is displayed.
                            status = PunchStatusEntries.STATUS_NOT_APPROVED_LEVEL1;
                        } else if (hasLevel2NotApproved) {
                            // 3 - If all Level 1 punches are approved, a green "1" sticker is displayed.
                            status = PunchStatusEntries.STATUS_APPROVED_LEVEL1;
                        } else if (isApproved) {
                            // 1 - A green check sticker is displayed during the day if all the day's punches are approved level 2
                            status = PunchStatusEntries.STATUS_APPROVED;
                        } else {
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        }
                    }
                }
            } else {
                // By project
                const hasLevel1 = list1.some(item => item.myL === 1) ||
                    list2.some(item => item.myL === 1) ||
                    listAp.some(item => item.myL === 1);

                const hasLevel2 = list1.some(item => item.myL === 2) ||
                    list2.some(item => item.myL === 2) ||
                    listAp.some(item => item.myL === 2);

                if (hasLevel1 && !hasLevel2) {
                    if (isLevel1ApprobationMandatory) {
                        // Only Level 1 punches during the day - Level 1 approbation mandatory
                        // 1 - Display a green check sticker during the day if all the punches for the day are Level 1 approved
                        // 2 - If one of the punches is not Level 1 approved, no sticker is displayed
                        isApproved = (list1.length == 0 || list1.every(item => item.isAp1)) &&
                            (list2.length == 0 || list2.every(item => item.isAp1));

                        if (isApproved) {
                            // 1 - Display a green check sticker during the day if all the punches for the day are Level 1 approved
                            status = PunchStatusEntries.STATUS_APPROVED;
                        } else {
                            // 2 - If one of the punches is not Level 1 approved, no sticker is displayed
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        }

                    } else {
                        // Only Level 1 punches during the day - Level 1 approbation NOT mandatory
                        // 1 - A green check sticker is displayed during the day if all punches for the day are approved at Level 1 or 2
                        // 2 - If one of the punches is not approved at Level 1, no sticker is displayed
                        isApproved = (list1.length == 0 || list1.every(item => item.isAp1 || item.isAp)) &&
                            (list2.length == 0 || list2.every(item => item.isAp1 || item.isAp));

                        if (isApproved) {
                            status = PunchStatusEntries.STATUS_APPROVED;
                        } else {
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        }
                    }
                } else if (!hasLevel1 && hasLevel2) {
                    if (isLevel1ApprobationMandatory) {
                        // Only Level 2 punches per day - Level 1 approbation mandatory
                        // 1 - Display a green check sticker during the day if all the punches for the day are approved at Level 2
                        // 2 - If one of the punches is not approved at Level 1, a red "1" sticker is displayed
                        // 3 - If all Level 1 punches are approved, a green "1" sticker is displayed
                        isApproved = (list1.length == 0 || list1.every(item => item.isAp)) &&
                            (list2.length == 0 || list2.every(item => item.isAp));

                        hasLevel1NotApproved = list1.some(item => !item.isAp1) || list2.some(item => !item.isAp1);
                        hasLevel2NotApproved = list1.some(item => !item.isAp) || list2.some(item => !item.isAp);

                        if (hasLevel1NotApproved) {
                            // 2 - If one of the punches is not approved at Level 1, a red "1" sticker is displayed
                            status = PunchStatusEntries.STATUS_NOT_APPROVED_LEVEL1;
                        } else if (hasLevel2NotApproved) {
                            // 3 - If all Level 1 punches are approved, a green "1" sticker is displayed
                            status = PunchStatusEntries.STATUS_APPROVED_LEVEL1;
                        } else if (isApproved) {
                            // 1 - Display a green check sticker during the day if all the punches for the day are approved at Level 2
                            status = PunchStatusEntries.STATUS_APPROVED;
                        } else {
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        }
                    } else {
                        // Only Level 2 punches during the day - Level 1 approbation NOT mandatory
                        // 1 - A green check sticker is displayed during the day if all the punches for the day are Level 2 approved
                        // 2 - If one of the punches is not Level 2 approved, no sticker is displayed
                        isApproved = listAp.length > 0;
                        hasLevel2NotApproved = list1.some(item => !item.isAp) || list2.some(item => !item.isAp);

                        if (hasLevel2NotApproved) {
                            // 2 - If one of the punches is not Level 2 approved, no sticker is displayed
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        } else if (isApproved) {
                            // 1 - A green check sticker is displayed during the day if all the punches for the day are Level 2 approved
                            status = PunchStatusEntries.STATUS_APPROVED;
                        } else {
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        }
                    }
                } else if (hasLevel1 && hasLevel2) {
                    if (isLevel1ApprobationMandatory) {
                        // Mixed Level 1 and 2 Punches During the Day - Level 1 approbation mandatory
                        // 1 - A green check mark is displayed during the day if all punches for the day are approved according to its level (all Level 1 and Level 2 punches are approved).
                        // 2 - If one of the punches is not approved at Level 1, a red "1" mark is displayed. Regardless of whether the other Level 1 and/or Level 2 punches are approved.
                        // 3 - If all Level 1 punches for the day are approved and one of the punches lacks Level 2 approval, a green "1" mark is displayed.
                        isApproved = listAp.length > 0;
                        hasLevel1NotApproved = list1.some(item => !item.isAp1) || list2.some(item => !item.isAp1);
                        hasLevel2NotApproved = list1.some(item => !item.isAp) || list2.some(item => !item.isAp);

                        if (hasLevel1NotApproved) {
                            // 2 - If one of the punches is not approved at Level 1, a red "1" mark is displayed. Regardless of whether the other Level 1 and/or Level 2 punches are approved.
                            status = PunchStatusEntries.STATUS_NOT_APPROVED_LEVEL1;
                        } else if (hasLevel2NotApproved) {
                            // 3 - If all Level 1 punches for the day are approved and one of the punches lacks Level 2 approval, a green "1" mark is displayed.
                            status = PunchStatusEntries.STATUS_APPROVED_LEVEL1;
                        } else if (isApproved) {
                            // 1 - A green check mark is displayed during the day if all punches for the day are approved according to its level (all Level 1 and Level 2 punches are approved).
                            status = PunchStatusEntries.STATUS_APPROVED;
                        } else {
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        }
                    } else {
                        // Mixed Level 1 and Level 2 punches during the day - Level 1 approbation NOT mandatory
                        // 1 - Display a green check sticker during the day if all the punches for the day are approved according to its level (all Level 1 and Level 2 punches are approved).
                        // 2 - If one of the punches is not approved at Level 1, a red "1" sticker is displayed.
                        // 3 - If one of the punches is not approved at Level 2, a green "1" sticker is displayed.
                        isApproved = listAp.length > 0;
                        hasLevel1NotApproved = list1.some(item => !item.isAp1) || list2.some(item => !item.isAp1);
                        hasLevel2NotApproved = list1.some(item => !item.isAp) || list2.some(item => !item.isAp);

                        if (hasLevel1NotApproved) {
                            // 2 - If one of the punches is not approved at Level 1, a red "1" sticker is displayed.
                            status = PunchStatusEntries.STATUS_NOT_APPROVED_LEVEL1;
                        } else if (hasLevel2NotApproved) {
                            // 3 - If one of the punches is not approved at Level 2, a green "1" sticker is displayed.
                            status = PunchStatusEntries.STATUS_APPROVED_LEVEL1;
                        } else if (isApproved) {
                            // 1 - Display a green check sticker during the day if all the punches for the day are approved according to its level (all Level 1 and Level 2 punches are approved).
                            status = PunchStatusEntries.STATUS_APPROVED;
                        } else {
                            status = PunchStatusEntries.STATUS_NOT_APPROVED;
                        }
                    }
                } else {
                    status = PunchStatusEntries.STATUS_NONE;
                }
            }
        } else {
            // Normal approbation
            // 1 - A green check mark is displayed during the day if all the punches for the day are approved
            // 2 - If one of the punches is not approved, no mark is displayed
            isApproved = listAp.length > 0;
            hasLevel2NotApproved = list2.some(item => !item.isAp);
            hasError = list2.some(item => item.status == PunchStatusEntries.STATUS_ERROR);
            if (hasLevel2NotApproved) {
                // 2 - If one of the punches is not approved, no mark is displayed
                if (hasError) {
                    status = PunchStatusEntries.STATUS_ERROR;
                } else {
                    status = PunchStatusEntries.STATUS_NOT_APPROVED;
                }
            } else if (isApproved) {
                // 1 - A green check mark is displayed during the day if all the punches for the day are approved
                status = PunchStatusEntries.STATUS_APPROVED;
            }
        }

        return { punchType: punchType, isApproved: isApproved, status: status }
    }

    return (
        <ul className={classes.ul}>
            {intervalDate.length > 0 && intervalDate.map((obj, index) => {
                const info = getInfoDate(obj.date);
                let textColor = '#000000';

                // Type Regular
                let typeDayClass = classes.none;

                if (info.punchType == PunchType.VACATION) {
                    typeDayClass = classes.vacation;

                    textColor = getContrastColor(storage.getVacColor());
                } else if (info.punchType == PunchType.ABSENCE) {
                    typeDayClass = classes.absence;

                    textColor = getContrastColor(storage.getAbsColor());
                }
                return (
                    <li key={index}>
                        <div className={`${classes.divDay} ${typeDayClass}`} style={{ color: textColor }}>
                            <span className={classes.spanDayDesc}>
                                <DescriptionWeekDay date={toISOTimezone(obj.date)} />
                            </span>
                            <span className={classes.spanDay}>{obj.date.getDate()}</span>
                        </div>
                        <div className={`${classes.divHour}`}>
                            <span className={classes.spanHour}>{info.hr}</span>
                            <span className={classes.spanHourLabel}>{i18next.t('punchApproval:label.hours')}</span>
                            {category != StatusEntries.Approved && info.status == PunchStatusEntries.STATUS_APPROVED && <div className={classes.divApproved}><FontAwesomeIcon icon={faCircleCheck} className={classes.iconApproved} /></div>}
                            {category != StatusEntries.Approved && info.status == PunchStatusEntries.STATUS_NOT_APPROVED_LEVEL1 && <div className={classes.divApproved}><FontAwesomeIcon icon={faCircle1} className={classes.iconNotApprovedLevel1} /></div>}
                            {category != StatusEntries.Approved && info.status == PunchStatusEntries.STATUS_APPROVED_LEVEL1 && <div className={classes.divApproved}><FontAwesomeIcon icon={faCircle1} className={classes.iconApprovedLevel1} /></div>}
                            {category != StatusEntries.Approved && info.status == PunchStatusEntries.STATUS_APPROVED_LEVEL2 && <div className={classes.divApproved}><FontAwesomeIcon icon={faCircle2} className={classes.iconApproved} /></div>}
                            {category != StatusEntries.Approved && info.status == PunchStatusEntries.STATUS_WARNING && <div className={classes.divApproved}><FontAwesomeIcon icon={faCircleExclamation} className={classes.iconWarning} /></div>}
                            {category != StatusEntries.Approved && info.status == PunchStatusEntries.STATUS_ERROR && <div className={classes.divApproved}><FontAwesomeIcon icon={faCircleXmark} className={classes.iconError} /></div>}
                            <span>{info.whatAmI}</span>
                        </div>
                    </li>
                )
            }
            )}
        </ul>
    );
}

export default withNamespaces(['scheduleAvailability', 'punchApproval', 'buttons'], { wait: true })(WeekDaysCalendar);